
import { Component, Emit, Prop, Vue, Watch } from "vue-property-decorator";
import vSelect3 from "vselect3";
import { Getter } from "vuex-class";
import { debounce } from "lodash";
import { getDebouncePeriod } from "@/utils/debounce-helper";
import api from "@/services/api.service";
import ShortContact from "@/types/sv-data/users/ShortContact";
import { truncateString } from "@/filters";

interface ShortContactSelectItem extends ShortContact {
	label: string;
}

@Component({
	components: {
		"v-select-3": vSelect3,
	},
	filters: { truncateString }
})
export default class ContactEmailSelect extends Vue {

	@Prop({type: Array, required: true, default: []})
	private value: ShortContact[];

	@Prop({type: Array, required: true})
	private contacts: ShortContact[];

	@Prop({ type: Boolean, default: false })
	private readOnly: boolean;

	@Prop({type: String, required: false, default: "Contacts / Email Address"})
	private label: string;

	@Prop({type: Boolean, required: false})
	private required: boolean;

	@Prop({type: String, required: false, default: "Select for Contact / Enter email directly"})
	private placeholder: string;

	@Getter("getFeature") getFeature: (featureName: string[]) => boolean;

    private localContacts: ShortContact[] = [];
	private invalidEmail: boolean = false;

	private mounted(): void {
		this.setContacts();
	}

	private get availableContacts(): ShortContact[] {
		const contactIds = this.value.map((c: ShortContact) => c.userID);
		const contactEmails = this.value.map((c: ShortContact) => c.email);
		return this.localContacts.filter((c: ShortContact) => !contactIds.includes(c.userID) && !contactEmails.includes(c.email));
	}

    private get userIds(): number[] {
		return this.value.filter((contact: ShortContact) => !!contact.userID).map((contact: ShortContact) => contact.userID);
    }

	@Watch("contacts")
	private onContactsUpdated(): void {
		this.setContacts();
	}

	private setContacts(): void {
		this.localContacts = [...this.contacts];
	}

	private getOptionLabel(option: ShortContact): string {
		if (option.fullName) {
			return option.fullName;
		}
		return option.email;
	}

    private isValidEmail(email: string): boolean {
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return re.test(email);
    }

    private async retrieveContacts(searchTerm: string): Promise<void> {
        try {
            const localContacts = await api.getContactsPaginated({
                params: {
                    page: 1,
                    pageSize: 100,
                    searchTerm,
					requiredUserIds: this.userIds
                },
                cancelToken: null
            });
			this.localContacts = [...localContacts];
        } catch (error) {
            console.error("Error when getting contacts", error)
        }
    }

	private onSearch(searchTerm: string): void {
		if (searchTerm) {
			this.handleSearchInput(searchTerm);
		} else {
			this.handleSearchInput.cancel();
			this.setContacts();
		}
	}

	private handleSearchInput = debounce(async (searchTerm: string): Promise<void> => {
			await this.retrieveContacts(searchTerm);
		},
		getDebouncePeriod(),
		{ leading: false, trailing: true }
	);

	@Emit("input")
	private async updateOptions(options: ShortContactSelectItem[]) {
		this.invalidEmail = false;
		const optionsCount = options.length;
		options = options.filter((option: ShortContactSelectItem) =>
			!!option.userID ||
			!!option.fullName ||
			!!option.email ||
			(!!option.label && this.isValidEmail(option.label)) ||
			// As emails can be entered manually this can also be a string
			(typeof option === "string" && this.isValidEmail(option))
		);

		if (optionsCount > options.length) {
			this.invalidEmail = true;
		}

		return options.map((option: ShortContactSelectItem) => {
			return {
				email: option.email ?? option.label ?? option ?? null,
				userID: option.userID ?? null,
				fullName: option.fullName ?? null
			}
		});
	}
}
